import { ReactElement, useCallback, useMemo } from 'react';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { styled } from 'styled-components';
import { blockStyle } from '../../../../../components/styles/Block';
import type { RecordingFilters } from '../../hooks/useRecordings';
import FormRow from './FormRow';
import ButtonRow from './ButtonRow';
import layoutStyle from '../../../../../components/styles/Layout';
import useAutoCompleteOptions from '../../hooks/useAutoCompleteOptions';

const Block = styled.section`
  ${blockStyle}
  background: #fff;
  border-radius: 8px;
`;

const Layout = styled.div`
  ${layoutStyle}
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface FormValues {
  customerName?: string | null;
  customerPhone?: string | null;
  advisorName?: string | null;
  recordingStatusId?: number | null;
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

interface Props {
  filters: RecordingFilters;
  onSearch: (filters: RecordingFilters) => void;
}

const SearchCondition = ({ filters, onSearch }: Props): ReactElement => {
  const [form] = Form.useForm<FormValues>();
  const { customerOptions, advisorOptions } = useAutoCompleteOptions();

  const initialValues: FormValues = useMemo(
    () => ({
      customerName: filters.customerName,
      customerPhone: filters.customerPhone,
      advisorName: filters.advisorName,
      recordingStatusId: filters.recordingStatusId,
      dateRange: filters.dateFrom && filters.dateTo ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)] : undefined,
    }),
    [filters]
  );

  const formValues = Form.useWatch<FormValues | undefined>([], form);

  const isFormModified = useMemo(() => {
    if (formValues === undefined) {
      return false;
    }
    return (
      (formValues.customerName != null && formValues.customerName.trim() !== '') ||
      (formValues.customerPhone != null && formValues.customerPhone.trim() !== '') ||
      (formValues.advisorName != null && formValues.advisorName.trim() !== '') ||
      formValues.recordingStatusId != null ||
      formValues.dateRange != null
    );
  }, [formValues]);

  const handleFinish = useCallback(
    (values: FormValues) => {
      const trimmedCustomerName = values.customerName?.trim();
      const trimmedCustomerPhone = values.customerPhone?.trim();
      const trimmedAdvisorName = values.advisorName?.trim();
      const newFilters: RecordingFilters = {
        customerName: trimmedCustomerName !== '' ? trimmedCustomerName : undefined,
        customerPhone: trimmedCustomerPhone !== '' ? trimmedCustomerPhone : undefined,
        advisorName: trimmedAdvisorName !== '' ? trimmedAdvisorName : undefined,
        recordingStatusId: values.recordingStatusId ?? undefined,
        dateFrom: values.dateRange?.[0]?.format('YYYY-MM-DD'),
        dateTo: values.dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      onSearch(newFilters);
    },
    [onSearch]
  );

  const handleClear = useCallback(() => {
    form.setFields([
      { name: 'customerName', value: null },
      { name: 'customerPhone', value: null },
      { name: 'advisorName', value: null },
      { name: 'recordingStatusId', value: null },
      { name: 'dateRange', value: null },
    ]);
  }, [form]);

  return (
    <Block>
      <Form form={form} initialValues={initialValues} onFinish={handleFinish}>
        <Layout>
          <FormRow customerOptions={customerOptions} advisorOptions={advisorOptions} />
          <ButtonRow isFormModified={isFormModified} onClear={handleClear} />
        </Layout>
      </Form>
    </Block>
  );
};

export default SearchCondition;
